import { isCashAlert, isDaysAlert } from './pinCard';

// DJB-243: the two pin styles on Accounts › Maps. Pure helpers (no Leaflet,
// no React) so the colour / icon rules are unit-testable; MachineMap.js
// turns `pinIconHtml` into an L.divIcon and MapFilterBar.js renders the
// Standard / Detail toggle next to the search bar.
//
// Standard (default): every pin in the accent blue, exactly as DJB-240.
// Detail: the pin borrows the card's trigger colours (pinCard.js) —
//   yellow + € icon   when cash > €150        (isCashAlert)
//   orange + clock    when days > 90          (isDaysAlert)
//   blue, no icon     when neither trips
// A machine that trips both keeps the cash colour + € icon (cash is the
// first figure on the card and the one that gets collected) and gets the
// `is-days` class on top, which MachineMap.css turns into an orange rim.

export const MARKER_STYLE_STANDARD = 'standard';
export const MARKER_STYLE_DETAIL = 'detail';
export const DEFAULT_MARKER_STYLE = MARKER_STYLE_STANDARD;

export const MARKER_STYLE_OPTIONS = Object.freeze([
  Object.freeze({ value: MARKER_STYLE_STANDARD, label: 'Standard' }),
  Object.freeze({ value: MARKER_STYLE_DETAIL, label: 'Detail' }),
]);

export const isMarkerStyle = (value) =>
  value === MARKER_STYLE_STANDARD || value === MARKER_STYLE_DETAIL;

// Which triggers a machine trips and the resulting pin tone.
export const pinVariant = (machine, style = DEFAULT_MARKER_STYLE) => {
  if (style !== MARKER_STYLE_DETAIL || !machine) {
    return { tone: 'default', cash: false, days: false };
  }
  const cash = isCashAlert(machine.cash);
  const days = isDaysAlert(machine.daysSinceCashout);
  return { tone: cash ? 'cash' : days ? 'days' : 'default', cash, days };
};

// Subtle vector glyphs (no emoji), drawn white-on-colour inside the pin
// head. 12×12 viewBox, stroke-based so they stay crisp at the 10px size.
export const EURO_ICON_SVG =
  '<svg class="machine-pin-glyph machine-pin-glyph-euro" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
  '<path d="M9.2 3.2A3.4 3.4 0 0 0 6.6 2C4.7 2 3.3 3.8 3.3 6s1.4 4 3.3 4a3.4 3.4 0 0 0 2.6-1.2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
  '<path d="M1.8 5h5M1.8 7h5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
  '</svg>';

export const CLOCK_ICON_SVG =
  '<svg class="machine-pin-glyph machine-pin-glyph-clock" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
  '<circle cx="6" cy="6" r="4.4" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
  '<path d="M6 3.4V6l1.9 1.3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

export const pinClassName = (variant) =>
  [
    'machine-pin',
    variant.tone !== 'default' && `is-${variant.tone}`,
    variant.cash && 'has-cash-trigger',
    variant.days && 'has-days-trigger',
  ]
    .filter(Boolean)
    .join(' ');

// The divIcon's inner HTML for one machine in the given style.
export const pinIconHtml = (machine, style = DEFAULT_MARKER_STYLE) => {
  const variant = pinVariant(machine, style);
  let glyph = '';
  if (variant.tone === 'cash') glyph = EURO_ICON_SVG;
  else if (variant.tone === 'days') glyph = CLOCK_ICON_SVG;
  return `<span class="${pinClassName(variant)}" aria-hidden="true">${glyph}</span>`;
};
